import { z } from 'zod';

import { Id, IsoDateTime, Paise } from '../common/primitives';

export { Paise } from '../common/primitives';

// ── Enums ──────────────────────────────────────────────────────────────────

export const SaasPlanCodeSchema = z.enum(['FREE', 'GROWTH', 'PRO', 'ENTERPRISE']);
export type SaasPlanCode = z.infer<typeof SaasPlanCodeSchema>;

export const SaasBillingCycleSchema = z.enum(['MONTHLY', 'ANNUAL']);
export type SaasBillingCycle = z.infer<typeof SaasBillingCycleSchema>;

export const VendorSubscriptionStatusSchema = z.enum(['TRIALING', 'ACTIVE', 'PAST_DUE', 'CANCELLED', 'EXPIRED']);
export type VendorSubscriptionStatus = z.infer<typeof VendorSubscriptionStatusSchema>;

export const SubscriptionInvoiceStatusSchema = z.enum(['PENDING', 'PAID', 'FAILED', 'REFUNDED', 'CANCELLED']);
export type SubscriptionInvoiceStatus = z.infer<typeof SubscriptionInvoiceStatusSchema>;

export const SponsoredListingStatusSchema = z.enum(['PAUSED', 'ACTIVE', 'COMPLETED', 'CANCELLED']);
export type SponsoredListingStatus = z.infer<typeof SponsoredListingStatusSchema>;

export const SponsoredEventTypeSchema = z.enum(['IMPRESSION', 'CLICK']);
export type SponsoredEventType = z.infer<typeof SponsoredEventTypeSchema>;

/**
 * Capability flags carried on `SaasPlan.features`. Adding a feature here is the only way to
 * gate a new product behaviour behind a plan — service code reads these via `hasFeature`.
 */
export const SaasFeatureSchema = z.enum([
  'analytics',
  'crm',
  'erp',
  'sponsoredListings',
  'apiAccess',
  'prioritySupport',
  'customDomain',
  'multiWarehouse',
]);
export type SaasFeature = z.infer<typeof SaasFeatureSchema>;

// ── Plan ───────────────────────────────────────────────────────────────────

export const SaasPlanSchema = z.object({
  id: Id,
  code: SaasPlanCodeSchema,
  name: z.string(),
  monthlyPricePaise: Paise,
  annualPricePaise: Paise,
  productCap: z.number().int(),
  mediaPerProductCap: z.number().int(),
  sponsoredFeeBps: z.number().int(),
  features: z.record(z.boolean()),
  active: z.boolean(),
  createdAt: IsoDateTime,
});
export type SaasPlan = z.infer<typeof SaasPlanSchema>;

export const UpsertPlanRequest = z
  .object({
    code: SaasPlanCodeSchema,
    name: z.string().trim().min(2).max(120),
    monthlyPricePaise: Paise.refine((v) => v >= 0),
    annualPricePaise: Paise.optional(),
    productCap: z.number().int().min(-1).max(1_000_000),
    mediaPerProductCap: z.number().int().min(-1).max(100).optional(),
    sponsoredFeeBps: z.number().int().min(0).max(5000).optional(),
    features: z.record(SaasFeatureSchema, z.boolean()).optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type UpsertPlanRequest = z.infer<typeof UpsertPlanRequest>;

// ── Subscription ───────────────────────────────────────────────────────────

export const VendorSubscriptionSchema = z.object({
  id: Id,
  vendorId: Id,
  planId: Id,
  status: VendorSubscriptionStatusSchema,
  billingCycle: SaasBillingCycleSchema,
  currentPeriodStart: IsoDateTime,
  currentPeriodEnd: IsoDateTime,
  trialEndsAt: IsoDateTime.nullable(),
  autoRenew: z.boolean(),
  providerSubscriptionId: z.string().nullable(),
  cancelledAt: IsoDateTime.nullable(),
  cancelReason: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type VendorSubscription = z.infer<typeof VendorSubscriptionSchema>;

export const SubscribeRequest = z
  .object({
    vendorId: Id,
    planCode: SaasPlanCodeSchema,
    billingCycle: SaasBillingCycleSchema.default('MONTHLY'),
    trialDays: z.number().int().min(0).max(60).optional(),
    autoRenew: z.boolean().optional(),
  })
  .strict();
export type SubscribeRequest = z.infer<typeof SubscribeRequest>;

export const ChangePlanRequest = z
  .object({
    planCode: SaasPlanCodeSchema,
    billingCycle: SaasBillingCycleSchema.optional(),
  })
  .strict();
export type ChangePlanRequest = z.infer<typeof ChangePlanRequest>;

export const CancelSubscriptionRequest = z
  .object({
    reason: z.string().trim().min(2).max(500),
    /// Immediate = end the entitlement now; deferred = let current period finish.
    immediate: z.boolean().default(false),
  })
  .strict();
export type CancelSubscriptionRequest = z.infer<typeof CancelSubscriptionRequest>;

// ── Invoice ────────────────────────────────────────────────────────────────

export const SubscriptionInvoiceSchema = z.object({
  id: Id,
  invoiceNumber: z.string(),
  subscriptionId: Id,
  periodStart: IsoDateTime,
  periodEnd: IsoDateTime,
  amountPaise: Paise,
  taxPaise: Paise,
  totalPaise: Paise,
  status: SubscriptionInvoiceStatusSchema,
  dueAt: IsoDateTime,
  paidAt: IsoDateTime.nullable(),
  providerPaymentId: z.string().nullable(),
  failureReason: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type SubscriptionInvoice = z.infer<typeof SubscriptionInvoiceSchema>;

export const RecordInvoicePaymentRequest = z
  .object({
    providerPaymentId: z.string().trim().max(120).optional(),
  })
  .strict();
export type RecordInvoicePaymentRequest = z.infer<typeof RecordInvoicePaymentRequest>;

// ── Feature gate / entitlement ────────────────────────────────────────────

export const EntitlementSchema = z.object({
  vendorId: Id,
  planCode: SaasPlanCodeSchema,
  status: VendorSubscriptionStatusSchema,
  productCap: z.number().int(),
  mediaPerProductCap: z.number().int(),
  sponsoredFeeBps: z.number().int(),
  features: z.record(z.boolean()),
  /// Convenience: true when status ∈ {TRIALING, ACTIVE}.
  active: z.boolean(),
  currentPeriodEnd: IsoDateTime,
});
export type Entitlement = z.infer<typeof EntitlementSchema>;

// ── Sponsored listings ────────────────────────────────────────────────────

export const SponsoredListingSchema = z.object({
  id: Id,
  vendorId: Id,
  productId: Id,
  status: SponsoredListingStatusSchema,
  bidPerClickPaise: Paise,
  totalBudgetPaise: Paise,
  spentPaise: Paise,
  impressions: z.number().int(),
  clicks: z.number().int(),
  startsAt: IsoDateTime,
  endsAt: IsoDateTime.nullable(),
  pausedReason: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type SponsoredListing = z.infer<typeof SponsoredListingSchema>;

export const CreateSponsoredRequest = z
  .object({
    productId: Id,
    bidPerClickPaise: Paise.refine((v) => v > 0),
    totalBudgetPaise: Paise.refine((v) => v > 0),
    startsAt: IsoDateTime.optional(),
    endsAt: IsoDateTime.optional(),
  })
  .strict()
  .refine((v) => v.totalBudgetPaise >= v.bidPerClickPaise, {
    message: 'totalBudgetPaise must cover at least one click',
    path: ['totalBudgetPaise'],
  });
export type CreateSponsoredRequest = z.infer<typeof CreateSponsoredRequest>;

export const RecordSponsoredEventRequest = z
  .object({
    sponsoredListingId: Id,
    type: SponsoredEventTypeSchema,
    userId: Id.optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .strict();
export type RecordSponsoredEventRequest = z.infer<typeof RecordSponsoredEventRequest>;

export const SponsoredListingEventSchema = z.object({
  id: Id,
  sponsoredListingId: Id,
  type: SponsoredEventTypeSchema,
  costPaise: Paise,
  userId: Id.nullable(),
  metadata: z.unknown().nullable(),
  createdAt: IsoDateTime,
});
export type SponsoredListingEvent = z.infer<typeof SponsoredListingEventSchema>;
